import environment from 'platform/utilities/environment';

import footerContent from 'platform/forms/components/FormFooter';
import { externalServices } from 'platform/monitoring/DowntimeNotification';
import { minimalHeaderFormConfigOptions } from 'platform/forms-system/src/js/patterns/minimal-header';
import {
  checkValidPagePath,
  getNextPagePath,
} from 'platform/forms-system/src/js/routing';
import manifest from '../manifest.json';
import { FORM_21_4502 } from '../definitions/constants';

import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import getHelp from '../../shared/components/GetFormHelp';
import transformForSubmit from './submit-transformer';
import prefillTransformer from './prefill-transformer';

import personalInfoBasic, {
  personalInfoBasicNewFlow,
} from '../pages/personalInfoBasic';
import identificationInformation from '../pages/identificationInformation';
import contactInfo from '../pages/contactInfo';
import address from '../pages/address';
import plannedAddress from '../pages/plannedAddress';
import applicationInformation, {
  applicationInformationNewFlow,
} from '../pages/applicationInformation';
import currentServiceStatus from '../pages/currentServiceStatus';
import veteranStatusInformation, {
  veteranStatusInformationNewFlow,
} from '../pages/veteranStatusInformation';
import veteranDisabilityCompensation, {
  veteranDisabilityCompensationNewFlow,
} from '../pages/veteranDisabilityCompensation';
import vehicleDetails from '../pages/vehicleDetails';
import vehicleUse from '../pages/vehicleUse';
import previousVehicleApplication, {
  previousVehicleApplicationNewFlow,
} from '../pages/previousVehicleApplication';
import certification from '../pages/certification';

const isNewFormFlowEnabled = formData =>
  formData?.form214502NewFormFlow === true;

const isLegacyFormFlowEnabled = formData => !isNewFormFlowEnabled(formData);

const isCurrentlyOnActiveDuty = formData => {
  const status = formData?.applicationInfo?.currentlyOnActiveDuty;
  return status === true || status === 'Y';
};

const isReleasedFromActiveDuty = formData => {
  const status = formData?.applicationInfo?.currentlyOnActiveDuty;
  return status === false || status === 'N';
};

const withReviewAriaLabel = (uiSchema, itemAriaLabel) => ({
  ...uiSchema,
  'ui:options': {
    ...(uiSchema['ui:options'] || {}),
    itemAriaLabel,
  },
});

const updateContactInfo = (oldData, newData) => {
  const alt = newData?.veteran?.alternatePhone;

  if (
    alt &&
    (!alt.contact || String(alt.contact).trim() === '') &&
    (alt.callingCode === null || alt.callingCode === undefined)
  ) {
    return {
      ...newData,
      veteran: {
        ...newData.veteran,
        alternatePhone: {
          ...alt,
          callingCode: 1,
          countryCode: alt.countryCode || 'US',
        },
      },
    };
  }

  return newData;
};

const updateNewFlowServiceStatus = (oldData, newData) => {
  if (isCurrentlyOnActiveDuty(newData)) {
    const applicationInfo = { ...(newData.applicationInfo || {}) };
    const veteran = { ...(newData.veteran || {}) };

    delete applicationInfo.placeOfRelease;
    delete applicationInfo.dateOfRelease;
    delete veteran.plannedAddressNotApplicable;

    return {
      ...newData,
      applicationInfo,
      veteran,
    };
  }

  if (isReleasedFromActiveDuty(newData)) {
    const veteran = { ...(newData.veteran || {}) };

    delete veteran.plannedAddress;
    delete veteran.plannedAddressNotApplicable;

    return {
      ...newData,
      veteran,
    };
  }

  return newData;
};

const handleFormLoaded = props => {
  const {
    returnUrl,
    router,
    routes,
    formData,
    formConfig: loadedFormConfig,
  } = props;
  const pageList = routes?.[routes.length - 1]?.pageList || [];
  const introPath = `${loadedFormConfig?.urlPrefix || '/'}introduction`;
  const fallbackPath =
    getNextPagePath(pageList, formData, introPath) || introPath;
  const safeReturnUrl =
    returnUrl && checkValidPagePath(pageList, formData, returnUrl)
      ? returnUrl
      : fallbackPath;

  router.push(safeReturnUrl);
};

const minimalHeaderOptions = minimalHeaderFormConfigOptions({
  breadcrumbList: [
    { href: '/', label: 'VA.gov home' },
    { href: '/forms', label: 'VA forms' },
    {
      href: '/forms/21-4502',
      label:
        'Application for Automobile or Other Conveyance and Adaptive Equipment',
    },
    {
      href: '/forms/21-4502/apply-automobile-and-adaptive-equipment',
      label: 'Apply for automobile allowance and adaptive equipment online',
    },
  ],
  wrapping: true,
});

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: `${environment.API_URL}/simple_forms_api/v1/simple_forms`,
  trackingPrefix: 'ss-4502-',
  defaultDefinitions: {},
  dev: {
    collapsibleNavLinks: true,
    showNavLinks: true,
  },
  introduction: IntroductionPage,
  confirmation: ConfirmationPage,
  preSubmitInfo: {
    statementOfTruth: {
      body: 'I confirm that the identifying information in this form is accurate and has been represented correctly.',
      messageAriaDescribedby:
        'I confirm that the identifying information in this form is accurate and has been represented correctly.',
      fullNamePath: 'veteran.fullName',
    },
  },
  formId: '21-4502',
  saveInProgress: {
    messages: {
      inProgress:
        'Your application for automobile or adaptive equipment is in progress.',
      expired:
        'Your saved application for automobile or adaptive equipment has expired.',
      saved:
        'Your application for automobile or adaptive equipment has been saved.',
    },
  },
  onFormLoaded: handleFormLoaded,
  version: 0,
  prefillEnabled: true,
  prefillTransformer,
  transformForSubmit,
  savedFormMessages: {
    notFound:
      'Please start over to complete your application for automobile or adaptive equipment.',
    noAuth:
      'Please sign in again to continue your application for automobile or adaptive equipment.',
  },
  hideUnauthedStartLink: true,
  title: FORM_21_4502.FORM_CONFIG.TITLE,
  customText: {
    appType: 'veteran application',
    finishAppLaterMessage: 'Finish this application later',
    submitButtonText: 'Submit Application',
    reviewPageTitle: 'Review and sign your application',
    reviewPageFormTitle: 'Review, sign, and submit',
  },
  showSaveLinkAfterButtons: false,
  ...minimalHeaderOptions,
  formOptions: {
    ...(minimalHeaderOptions.formOptions || {}),
    enableChapterDepends: true,
  },

  chapters: {
    veteranIdChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_VETERAN_ID,
      depends: isLegacyFormFlowEnabled,
      pages: {
        personalInfoBasicPage: {
          path: 'personal-information',
          title: FORM_21_4502.FORM_CONFIG.PAGE_PERSONAL_INFO,
          uiSchema: withReviewAriaLabel(
            personalInfoBasic.uiSchema,
            FORM_21_4502.PERSONAL_INFO.REVIEW_EDIT_LABEL,
          ),
          schema: personalInfoBasic.schema,
        },
      },
    },
    basicInformationChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_VETERAN_ID,
      depends: isNewFormFlowEnabled,
      pages: {
        personalInfoBasicNewFlowPage: {
          path: 'name-and-date-of-birth',
          title: FORM_21_4502.FORM_CONFIG.PAGE_PERSONAL_INFO,
          uiSchema: withReviewAriaLabel(
            personalInfoBasicNewFlow.uiSchema,
            FORM_21_4502.PERSONAL_INFO.REVIEW_EDIT_LABEL,
          ),
          schema: personalInfoBasicNewFlow.schema,
        },
      },
    },

    identificationInformationChapter: {
      title: FORM_21_4502.IDENTIFICATION_INFORMATION.STEP_LABEL,
      depends: isNewFormFlowEnabled,
      pages: {
        identificationInformationPage: {
          path: 'identification-information',
          title: FORM_21_4502.IDENTIFICATION_INFORMATION.TITLE,
          uiSchema: withReviewAriaLabel(
            identificationInformation.uiSchema,
            FORM_21_4502.IDENTIFICATION_INFORMATION.REVIEW_EDIT_LABEL,
          ),
          schema: identificationInformation.schema,
        },
      },
    },
    contactChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_CONTACT,
      pages: {
        contactInfoPage: {
          path: 'contact-information',
          title: FORM_21_4502.FORM_CONFIG.PAGE_CONTACT,
          uiSchema: withReviewAriaLabel(
            contactInfo.uiSchema,
            FORM_21_4502.CONTACT_INFO.REVIEW_EDIT_LABEL,
          ),
          schema: contactInfo.schema,
          updateFormData: updateContactInfo,
        },
      },
    },
    addressChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_ADDRESS,
      pages: {
        addressPage: {
          path: 'address',
          title: FORM_21_4502.FORM_CONFIG.PAGE_ADDRESS,
          uiSchema: withReviewAriaLabel(
            address.uiSchema,
            FORM_21_4502.ADDRESS.REVIEW_EDIT_LABEL,
          ),
          schema: address.schema,
        },
      },
    },

    serviceStatusChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_SERVICE_STATUS,
      depends: isLegacyFormFlowEnabled,
      pages: {
        plannedAddressPage: {
          path: 'planned-address',
          title: FORM_21_4502.FORM_CONFIG.PAGE_PLANNED_ADDRESS,
          uiSchema: withReviewAriaLabel(
            plannedAddress.uiSchema,
            FORM_21_4502.PLANNED_ADDRESS.REVIEW_EDIT_LABEL,
          ),
          schema: plannedAddress.schema,
          updateFormData: plannedAddress.updateFormData,
        },
      },
    },
    applicationAndServiceInformationChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_APPLICATION,
      depends: isLegacyFormFlowEnabled,
      pages: {
        applicationInformationPage: {
          path: 'application-information',
          title: FORM_21_4502.FORM_CONFIG.PAGE_APPLICATION_INFO,
          uiSchema: withReviewAriaLabel(
            applicationInformation.uiSchema,
            FORM_21_4502.APPLICATION_INFORMATION.REVIEW_EDIT_LABEL,
          ),
          schema: applicationInformation.schema,
        },
      },
    },
    serviceInformationChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_APPLICATION,
      depends: isNewFormFlowEnabled,
      pages: {
        applicationInformationNewFlowPage: {
          path: 'service-information',
          title: FORM_21_4502.FORM_CONFIG.PAGE_APPLICATION_INFO,
          uiSchema: withReviewAriaLabel(
            applicationInformationNewFlow.uiSchema,
            FORM_21_4502.APPLICATION_INFORMATION.REVIEW_EDIT_LABEL,
          ),
          schema: applicationInformationNewFlow.schema,
        },
      },
    },
    currentServiceStatusChapter: {
      title: FORM_21_4502.CURRENT_SERVICE_STATUS.STEP_LABEL,
      depends: isNewFormFlowEnabled,
      pages: {
        currentServiceStatusNewFlowPage: {
          path: 'service-status',
          title: FORM_21_4502.FORM_CONFIG.PAGE_CURRENT_SERVICE,
          uiSchema: withReviewAriaLabel(
            currentServiceStatus.uiSchema,
            FORM_21_4502.CURRENT_SERVICE_STATUS.REVIEW_EDIT_LABEL,
          ),
          schema: currentServiceStatus.schema,
          updateFormData: updateNewFlowServiceStatus,
        },
      },
    },
    plannedAddressChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_SERVICE_STATUS,
      depends: formData =>
        isNewFormFlowEnabled(formData) && isCurrentlyOnActiveDuty(formData),
      pages: {
        plannedAddressNewFlowPage: {
          path: 'planned-address-after-active-duty',
          title: FORM_21_4502.FORM_CONFIG.PAGE_PLANNED_ADDRESS,
          uiSchema: withReviewAriaLabel(
            plannedAddress.uiSchema,
            FORM_21_4502.PLANNED_ADDRESS.REVIEW_EDIT_LABEL,
          ),
          schema: plannedAddress.schema,
          updateFormData: plannedAddress.updateFormData,
        },
      },
    },
    veteranServiceRecordChapter: {
      title: FORM_21_4502.VETERAN_STATUS_INFORMATION.STEP_LABEL,
      depends: formData =>
        isNewFormFlowEnabled(formData) && isReleasedFromActiveDuty(formData),
      pages: {
        veteranStatusInformationNewFlowPage: {
          path: 'release-from-active-duty',
          title: FORM_21_4502.FORM_CONFIG.PAGE_VETERAN_STATUS,
          uiSchema: withReviewAriaLabel(
            veteranStatusInformationNewFlow.uiSchema,
            FORM_21_4502.VETERAN_STATUS_INFORMATION.REVIEW_EDIT_LABEL,
          ),
          schema: veteranStatusInformationNewFlow.schema,
        },
      },
    },
    disabilityCompensationChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_QUALIFYING,
      depends: isNewFormFlowEnabled,
      pages: {
        veteranDisabilityCompensationNewFlowPage: {
          path: 'prior-disability-compensation-application',
          title: FORM_21_4502.FORM_CONFIG.PAGE_DISABILITY,
          uiSchema: withReviewAriaLabel(
            veteranDisabilityCompensationNewFlow.uiSchema,
            FORM_21_4502.VETERAN_DISABILITY_COMPENSATION.REVIEW_EDIT_LABEL,
          ),
          schema: veteranDisabilityCompensationNewFlow.schema,
        },
      },
    },
    serviceRecordChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_SERVICE_RECORD,
      depends: isLegacyFormFlowEnabled,
      pages: {
        currentServiceStatusPage: {
          path: 'current-service-status',
          title: FORM_21_4502.FORM_CONFIG.PAGE_CURRENT_SERVICE,
          uiSchema: withReviewAriaLabel(
            currentServiceStatus.uiSchema,
            FORM_21_4502.CURRENT_SERVICE_STATUS.REVIEW_EDIT_LABEL,
          ),
          schema: currentServiceStatus.schema,
        },
        veteranStatusInformationPage: {
          path: 'veteran-status-information',
          title: FORM_21_4502.FORM_CONFIG.PAGE_VETERAN_STATUS,
          uiSchema: withReviewAriaLabel(
            veteranStatusInformation.uiSchema,
            FORM_21_4502.VETERAN_STATUS_INFORMATION.REVIEW_EDIT_LABEL,
          ),
          schema: veteranStatusInformation.schema,
          depends: formData =>
            formData?.applicationInfo?.currentlyOnActiveDuty !== true,
        },
        veteranDisabilityCompensationPage: {
          path: 'veteran-disability-compensation',
          title: FORM_21_4502.FORM_CONFIG.PAGE_DISABILITY,
          uiSchema: withReviewAriaLabel(
            veteranDisabilityCompensation.uiSchema,
            FORM_21_4502.VETERAN_DISABILITY_COMPENSATION.REVIEW_EDIT_LABEL,
          ),
          schema: veteranDisabilityCompensation.schema,
          depends: formData =>
            formData?.applicationInfo?.currentlyOnActiveDuty !== true,
        },
      },
    },
    conveyanceTypeChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_CONVEYANCE,
      pages: {
        vehicleDetailsPage: {
          path: 'vehicle-details',
          title: FORM_21_4502.FORM_CONFIG.PAGE_VEHICLE_DETAILS,
          uiSchema: withReviewAriaLabel(
            vehicleDetails.uiSchema,
            FORM_21_4502.VEHICLE_DETAILS.REVIEW_EDIT_LABEL,
          ),
          schema: vehicleDetails.schema,
        },
      },
    },
    vehicleUseChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_VEHICLE_USE,
      pages: {
        vehicleUsePage: {
          path: 'vehicle-use',
          title: FORM_21_4502.FORM_CONFIG.PAGE_VEHICLE_USE,
          uiSchema: withReviewAriaLabel(
            vehicleUse.uiSchema,
            FORM_21_4502.VEHICLE_USE.REVIEW_EDIT_LABEL,
          ),
          schema: vehicleUse.schema,
        },
      },
    },
    previousVehicleApplicationChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_PREVIOUS_VEHICLE,
      depends: isLegacyFormFlowEnabled,
      pages: {
        previousVehicleApplicationPage: {
          path: 'previous-vehicle-application',
          title: FORM_21_4502.FORM_CONFIG.PAGE_PREVIOUS_VEHICLE,
          uiSchema: withReviewAriaLabel(
            previousVehicleApplication.uiSchema,
            FORM_21_4502.PREVIOUS_VEHICLE_APPLICATION.REVIEW_EDIT_LABEL,
          ),
          schema: previousVehicleApplication.schema,
        },
      },
    },

    previousVehicleApplicationNewFlowChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_PREVIOUS_VEHICLE,
      depends: isNewFormFlowEnabled,
      pages: {
        previousVehicleApplicationNewFlowPage: {
          path: 'vehicle-application-history',
          title: FORM_21_4502.FORM_CONFIG.PAGE_PREVIOUS_VEHICLE,
          uiSchema: withReviewAriaLabel(
            previousVehicleApplicationNewFlow.uiSchema,
            FORM_21_4502.PREVIOUS_VEHICLE_APPLICATION.REVIEW_EDIT_LABEL,
          ),
          schema: previousVehicleApplicationNewFlow.schema,
        },
      },
    },
    certificationChapter: {
      title: FORM_21_4502.FORM_CONFIG.CHAPTER_CERTIFICATION,
      pages: {
        certificationPage: {
          path: 'certification',
          title: FORM_21_4502.FORM_CONFIG.PAGE_CERTIFICATION,
          uiSchema: withReviewAriaLabel(
            certification.uiSchema,
            FORM_21_4502.CERTIFICATION.REVIEW_EDIT_LABEL,
          ),
          schema: certification.schema,
        },
      },
    },
  },
  downtime: {
    dependencies: [externalServices.lighthouseBenefitsIntake],
  },
  footerContent,
  getHelp,
};

export default formConfig;
